import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

const faqs = [
  {
    question: 'How old do I need to be to start driving lessons?',
    answer: 'In Virginia, teens can begin driver education at age 15.5. Adult learners can start at any age. We offer programs tailored to both teen and adult learners.',
  },
  {
    question: 'How long does the driving course take to complete?',
    answer: 'The Teen Complete program typically takes 6-8 weeks to complete, including 30 hours of classroom instruction and 14 hours of behind-the-wheel training. Adult programs can be completed more quickly based on your schedule.',
  },
  {
    question: 'Do you provide a car for the road test?',
    answer: 'Yes! Our Premium Package includes the use of a school vehicle for your DMV road test. We also offer this service as an add-on for other packages.',
  },
  {
    question: 'Are your instructors certified?',
    answer: 'All our instructors are DMV-certified and have extensive experience in driver education. They undergo regular training to stay current with the latest teaching methods and safety standards.',
  },
]

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="text-accent font-heading font-semibold text-sm uppercase tracking-wider mb-2">
            FAQ
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-secondary-text max-w-2xl mx-auto">
            Get answers to common questions about our programs.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-soft overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                aria-expanded={openIndex === index}
              >
                <span className="font-heading font-semibold text-primary pr-4">
                  {faq.question}
                </span>
                <ChevronDownIcon
                  className={`w-5 h-5 text-primary flex-shrink-0 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {/* @ts-ignore - framer-motion AnimatePresence type issue */}
              <AnimatePresence mode="wait" initial={false}>
                {openIndex === index && (
                  <motion.div
                    key={index}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-4 text-secondary-text">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* View All Link */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-8"
        >
          <Link
            to="/faq"
            className="text-primary font-heading font-semibold hover:text-accent transition-colors inline-flex items-center gap-1"
          >
            View All FAQs <span>&gt;</span>
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

